"use client"

import React, { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DatePicker } from "@/components/ui/date-picker"
import { useEngagementLookups } from "@/hooks/use-engagement-lookups"
import { useLanguage } from "@/contexts/language-context"
import { createCompleteEngagementSchema } from "@/lib/schemas/core-form-schema"
import { ENGAGEMENT_LOOKUP_CATEGORIES } from "@/lib/constants/engagement-codes"

type CompleteEngagementFormData = z.infer<ReturnType<typeof createCompleteEngagementSchema>>

export interface CompleteEngagementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (closedDate: Date | null, closedReason: string | null) => Promise<void>
  currentClosedDate?: Date | null
  currentClosedReason?: string | null
  startDate: Date | null
  endDate: Date | null
}

export function CompleteEngagementDialog({
  open,
  onOpenChange,
  onConfirm,
  currentClosedDate = null,
  currentClosedReason = null,
  startDate,
  endDate
}: CompleteEngagementDialogProps) {
  const { dictionary } = useLanguage()
  const { getByCategory: getEngagementLookupsByCategory } = useEngagementLookups()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Create resolver with current start and end dates for validation
  const completeEngagementResolver = useMemo(
    () => zodResolver(createCompleteEngagementSchema(startDate, endDate)),
    [startDate, endDate]
  )

  const form = useForm<CompleteEngagementFormData>({
    resolver: completeEngagementResolver,
    defaultValues: {
      closedDate: currentClosedDate,
      closedReason: currentClosedReason || ''
    },
    mode: 'onChange'
  })

  // Reset form with current values when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      // Reset form with current engagement values when opening
      form.reset({
        closedDate: currentClosedDate,
        closedReason: currentClosedReason || ''
      })
    } else {
      // Reset submitting state when closing
      setIsSubmitting(false)
    }
  }, [open, currentClosedDate, currentClosedReason, form])

  const watchedValues = form.watch(['closedDate'])
  const [closedDate] = watchedValues

  // Trigger closedReason validation when closedDate changes
  React.useEffect(() => {
    form.trigger('closedReason')
  }, [closedDate, form])

  const handleSubmit = async (data: CompleteEngagementFormData) => {
    try {
      setIsSubmitting(true)
      await onConfirm(data.closedDate, data.closedReason || null)
      onOpenChange(false)
    } catch (error) {
      // Error handling is done in parent
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark Engagement as Complete</DialogTitle>
          <DialogDescription>
            Enter the completion details for this engagement.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="closedDate"
              render={({ field }) => (
                <FormItem className="space-y-2.5">
                  <FormLabel className="text-sm font-medium">
                    {dictionary?.engagement?.core?.closedDate || "Project Completion Date"}
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      {...field}
                      placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                      minDate={startDate || undefined}
                      maxDate={endDate || undefined}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {closedDate && (
              <FormField
                control={form.control}
                name="closedReason"
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.closedReason || "Closed Reason"}
                    </FormLabel>
                    <FormControl>
                      <Select 
                        {...field} 
                        onValueChange={field.onChange}
                        allowClear
                      >
                        <SelectTrigger className="form-field-focus">
                          <SelectValue placeholder={dictionary?.engagement?.core?.selectReason || "Select a reason"} />
                        </SelectTrigger>
                        <SelectContent>
                          {getEngagementLookupsByCategory(ENGAGEMENT_LOOKUP_CATEGORIES.CLOSED_REASON).map(reason => (
                            <SelectItem key={reason.code} value={reason.code!}>
                              {reason.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Completing...' : 'Complete Engagement'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

